import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { animate, inView } from 'motion';
import type { RouteStop } from './data';
import { SL_DISTRICT_PATHS, SL_VIEWBOX } from './sri-lanka-geo';

/**
 * The site's signature device: itinerary stops plotted on the real outline of
 * Sri Lanka (simplified from actual district boundary data, not a stylised
 * blob), connected by a glowing route line that draws itself in on scroll.
 */
@Component({
  selector: 'app-island-map',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative">
      <svg
        #svg
        [attr.viewBox]="'0 0 ' + vb.width + ' ' + vb.height"
        class="w-full h-auto overflow-visible relative z-10"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="routeGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="var(--color-saffron)" />
            <stop offset="100%" stop-color="var(--color-vermilion)" />
          </linearGradient>
          <filter id="islandShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="6" stdDeviation="10" flood-color="#16130f" flood-opacity="0.28" />
          </filter>
          <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="var(--color-vermilion)" stop-opacity="0.55" />
            <stop offset="100%" stop-color="var(--color-vermilion)" stop-opacity="0" />
          </radialGradient>
        </defs>

        <g filter="url(#islandShadow)">
          <g fill="var(--color-ink)" fill-opacity="0.08" stroke="var(--color-ink)" stroke-opacity="0.08" stroke-width="5" stroke-linejoin="round">
            @for (d of districtPaths; track $index) {
              <path [attr.d]="d" />
            }
          </g>
          <g fill="var(--color-paper-soft)" stroke="var(--color-sand)" stroke-width="4.5" stroke-linejoin="round" transform="translate(-2,-3)">
            @for (d of districtPaths; track $index) {
              <path [attr.d]="d" />
            }
          </g>
        </g>

        <path
          #route
          id="island-route-path"
          [attr.d]="routePath()"
          fill="none"
          stroke="url(#routeGradient)"
          stroke-width="4.2"
          stroke-linecap="round"
          stroke-dasharray="1 14"
        />

        @if (trainVisible()) {
          <g>
            <circle r="9" fill="var(--color-paper)" stroke="var(--color-vermilion)" stroke-width="1.6" />
            <g transform="translate(-9,-9) scale(0.75)" fill="none" stroke="var(--color-ink)" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 3.5h12a1.5 1.5 0 0 1 1.5 1.5v9a3.5 3.5 0 0 1-3.5 3.5H8A3.5 3.5 0 0 1 4.5 14V5A1.5 1.5 0 0 1 6 3.5Z M4.5 10h15 M8 20.5 6 22.5 M16 20.5l2 2 M8.5 14.5h.01 M15.5 14.5h.01" />
            </g>
            <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
              <mpath href="#island-route-path" />
            </animateMotion>
          </g>
        }

        @for (stop of stops(); track stop.code) {
          <g>
            @if (stop.code === activeCode()) {
              <circle [attr.cx]="stop.x" [attr.cy]="stop.y" r="26" fill="url(#pinGlow)" />
            }
            <circle
              [attr.cx]="stop.x"
              [attr.cy]="stop.y"
              [attr.r]="stop.code === activeCode() ? 8.5 : 6.5"
              [attr.fill]="stop.code === activeCode() ? 'var(--color-vermilion)' : 'var(--color-ink)'"
              stroke="var(--color-paper)"
              stroke-width="2.4"
              style="transition: r 0.25s ease, fill 0.25s ease;"
            />
            <text
              [attr.x]="stop.x + 12"
              [attr.y]="stop.y + 4"
              class="route-code"
              font-size="15"
              [attr.fill]="stop.code === activeCode() ? 'var(--color-vermilion)' : 'var(--color-ink)'"
              style="transition: fill 0.25s ease; paint-order: stroke; stroke: var(--color-paper); stroke-width: 3px;"
            >
              {{ stop.code }}
            </text>
          </g>
        }
      </svg>
    </div>
  `,
})
export class IslandMap {
  private platformId = inject(PLATFORM_ID);
  private svgEl = viewChild<ElementRef<SVGSVGElement>>('svg');
  private routeEl = viewChild<ElementRef<SVGPathElement>>('route');

  stops = input.required<RouteStop[]>();
  activeCode = input<string | undefined>(undefined);

  districtPaths = SL_DISTRICT_PATHS;
  vb = SL_VIEWBOX;
  trainVisible = signal(false);

  routePath() {
    const pts = this.stops();
    if (!pts.length) return '';
    return pts.map((s, i) => `${i === 0 ? 'M' : 'L'}${s.x},${s.y}`).join(' ');
  }

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const path = this.routeEl()?.nativeElement;
      if (!path) return;

      const length = path.getTotalLength();
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = reduced ? '0' : `${length}`;

      if (reduced) return;

      const svg = this.svgEl()?.nativeElement;
      if (!svg) return;

      inView(
        svg,
        () => {
          animate(path, { strokeDashoffset: [length, 0] } as never, {
            duration: 1.8,
            ease: [0.22, 1, 0.36, 1],
          });
          setTimeout(() => {
            path.style.strokeDasharray = '1 14';
            this.trainVisible.set(true);
          }, 1850);
        },
        { amount: 0.3 },
      );
    });
  }
}
